import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PremiseConfig } from "@shared/types/foyer";
import type { PremiseCommit } from "../context/PremiseWorkspace";
import { FONT_MONO } from "../theme";

interface Props {
  premise: PremiseConfig;
  /** Persist one change now and toast (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}

/**
 * Manage the per-premise `demoIpRanges`. A TV pairing from a matching IPv4
 * CIDR is tagged `demo:true` and may be paired into a DEMO UNIT, where it is
 * licence-free (UNIT-5). From these ranges into a normal unit it uses a seat.
 *
 * Adding or removing a range saves at once and toasts - there is no Save to
 * press. Validation is intentionally loose - bad entries just don't match
 * anything.
 */
export default function DemoIpRangesEditor({ premise, onCommit }: Props) {
  const { t } = useTranslation(["users", "common"]);
  const [draft, setDraft] = useState("");
  const ranges = premise.demoIpRanges ?? [];

  const name = premise.premiseName;

  function add() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (ranges.includes(trimmed)) {
      setDraft("");
      return;
    }
    setDraft("");
    void onCommit(
      (c) => {
        const cur = c.demoIpRanges ?? [];
        return cur.includes(trimmed)
          ? c
          : { ...c, demoIpRanges: [...cur, trimmed] };
      },
      {
        success: t("demoIpRangesEditor.added", { range: trimmed, name }),
        failure: "addDemoIpRange",
        failureParams: { range: trimmed, name },
      }
    );
  }

  function remove(range: string) {
    void onCommit(
      (c) => ({
        ...c,
        demoIpRanges: (c.demoIpRanges ?? []).filter((r) => r !== range),
      }),
      {
        success: t("demoIpRangesEditor.removed", { range, name }),
        failure: "removeDemoIpRange",
        failureParams: { range, name },
      }
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("demoIpRangesEditor.title")}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2 }}
        data-testid="demo-ranges-help"
      >
        {t("demoIpRangesEditor.help")}
      </Typography>

      <Stack spacing={1} sx={{ mb: 2 }}>
        {ranges.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
          >
            {t("demoIpRangesEditor.none")}
          </Typography>
        )}
        {ranges.map((r, i) => (
          <Stack
            key={`${r}-${i}`}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
            data-testid={`demo-range-row-${i}`}
          >
            <Chip
              size="small"
              label={r}
              sx={{ fontFamily: "fontFamilyMono" }}
            />
            <Box sx={{ flex: 1 }} />
            <Tooltip title={t("common:buttons.remove")}>
              <IconButton
                size="small"
                aria-label={t("demoIpRangesEditor.removeRange", { range: r })}
                onClick={() => remove(r)}
                data-testid={`demo-range-remove-${i}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1}>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={t("demoIpRangesEditor.placeholder")}
          size="small"
          sx={{ flex: 1 }}
          inputProps={{
            "data-testid": "demo-range-input",
            style: { fontFamily: FONT_MONO },
          }}
        />
        <Button
          variant="outlined"
          onClick={add}
          startIcon={<AddIcon />}
          disabled={!draft.trim()}
          data-testid="demo-range-add"
        >
          {t("common:buttons.add")}
        </Button>
      </Stack>
    </Paper>
  );
}
